import { createMachine } from 'xstate';
import { Auth, CognitoUser } from '@aws-amplify/auth';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import {
  GetMeQuery,
  Invitation,
  MyUserDataFragment,
  UpdateFederatedIdentityInformationInput,
} from '@/generated/codegen';

type GetMeResponse = NonNullable<GetMeQuery['getMe']>;
export type PartialProfileData = NonNullable<
  Extract<
    GetMeResponse,
    { __typename: 'ProfileDataMissingError' }
  >['partial_profile']
>;

export const constants = {
  STATE_SIGNIN: 'signIn',
  STATE_SIGNUP: 'signUp',
  STATE_CONFIRM_EMAIL: 'confirmEmail',
  STATE_FORGOT_PASSWORD: 'forgotPassword',
  STATE_SIGNED_IN: 'signedIn',
  STATE_LOGGED_IN: 'loggedIn',
  STATE_COMPLETE_PROFILE: 'completeProfile',
} as const;

const completeProfileStates = {
  id: constants.STATE_COMPLETE_PROFILE,
  entry: ['done'],
  exit: ['resetDelay', 'clearError'],
  initial: 'idle' as const,
  states: {
    idle: {
      on: {
        SUBMIT: {
          target: 'do_submit',
        },
        SIGNOUT: {
          actions: ['signOut'],
          target: '#' + constants.STATE_SIGNIN,
        },
      },
    },
    throttle: {
      exit: 'increaseDelay',
      after: {
        SIGNIN_THROTTLE_DELAY: 'do_submit',
      },
    },
    do_submit: {
      entry: 'loading',
      exit: 'done',
      invoke: {
        id: 'submit',
        src: 'submitProfileData',
        onDone: [
          {
            target: '#' + constants.STATE_SIGNED_IN,
          },
        ],
        onError: [
          {
            target: 'throttle',
            cond: 'isNetworkError',
            //TODO: when to retry
          },
          {
            target: 'idle',
            actions: 'setError',
          },
        ],
      },
    },
  },
};

const signInStates = {
  id: constants.STATE_SIGNIN,
  entry: ['saveDestination', 'rememberDestination', 'redirectToLogin', 'done'],
  exit: ['resetDelay', 'clearError'],
  initial: 'idle' as const,
  on: {
    RESET: {
      target: '.idle',
      internal: false,
    },
  },
  states: {
    idle: {
      on: {
        SIGNIN: {
          target: 'do_signin',
          actions: 'assignSignInInputs',
        },
        SIGNUP: '#' + constants.STATE_SIGNUP,
        FORGOT_PASSWORD: '#' + constants.STATE_FORGOT_PASSWORD,
      },
    },
    maybe_email_not_confirmed: {},
    throttle: {
      exit: 'increaseDelay',
      after: {
        SIGNIN_THROTTLE_DELAY: 'do_signin',
      },
    },
    do_signin: {
      entry: 'loading',
      exit: 'done',
      invoke: {
        id: 'signIn',
        src: 'signIn',
        onDone: [
          {
            target: '#' + constants.STATE_SIGNED_IN,
            actions: 'assignAuthData',
          },
        ],
        onError: [
          {
            // Cognito only sends the code on signUp; an unconfirmed user
            // signing in has to request a new one.
            target: '#' + constants.STATE_CONFIRM_EMAIL + '.resend',
            cond: 'isUserNotConfirmedException',
          },
          {
            target: 'throttle',
            cond: 'isNetworkError',
            //TODO: when to retry
          },
          {
            target: 'maybe_email_not_confirmed',
            actions: 'setError',
            cond: 'isUserNotFoundAndEmail',
          },
          {
            target: 'idle',
            actions: 'setError',
          },
        ],
      },
    },
  },
};

const forgotPasswordStates = {
  id: constants.STATE_FORGOT_PASSWORD,
  initial: 'idle' as const,
  exit: ['clearError'],
  states: {
    idle: {
      exit: ['clearError'],
      on: {
        FORGOT_PASSWORD: {
          target: 'send_code',
          actions: 'assignForgotPasswordInputs',
        },
        SIGNIN: '#' + constants.STATE_SIGNIN,
      },
    },
    send_code: {
      entry: 'loading',
      exit: 'done',
      invoke: {
        id: 'forgotPassword',
        src: 'forgotPassword',
        onDone: [
          {
            target: 'choose_password',
            actions: 'assignForgotPasswordDelivery',
          },
        ],
        onError: [
          {
            // Cognito can't send the reset code to an unverified email; the
            // confirmation email verifies it. The error stays in case the
            // user is already confirmed and the resend fails.
            target: 'resend_confirmation',
            cond: 'isUnverifiedEmailException',
            actions: ['assignForgotPasswordUsernameAsUsername', 'setError'],
          },
          {
            target: 'idle',
            actions: 'setError',
          },
        ],
      },
    },
    resend_confirmation: {
      entry: 'loading',
      exit: 'done',
      invoke: {
        id: 'resendConfirmation',
        src: 'resendConfirmation',
        onDone: {
          target: '#' + constants.STATE_CONFIRM_EMAIL + '.idle',
          actions: 'assignEmailConfirmationDestination',
        },
        onError: {
          target: 'idle',
        },
      },
    },
    choose_password: {
      on: {
        FORGOT_PASSWORD: {
          target: 'reset_password',
          actions: 'assignForgotPasswordInputs',
        },
        SIGNIN: '#' + constants.STATE_SIGNIN,
      },
    },
    reset_password: {
      entry: 'loading',
      exit: 'done',
      invoke: {
        id: 'forgotPasswordSubmit',
        src: 'forgotPasswordSubmit',
        onDone: [
          {
            target: '#' + constants.STATE_SIGNIN,
          },
        ],
        onError: [
          {
            target: 'idle',
            actions: 'setError',
          },
        ],
      },
    },
  },
};

const signUpStates = {
  id: constants.STATE_SIGNUP,
  initial: 'idle' as const,
  exit: ['clearError'],
  states: {
    idle: {
      exit: ['clearError'],
      on: {
        SIGNUP: {
          target: 'cognitoSignup',
          actions: 'assignSignUpInputs',
        },
        SIGNIN: '#' + constants.STATE_SIGNIN,
      },
    },
    cognitoSignup: {
      entry: 'loading',
      exit: 'done',
      invoke: {
        id: 'signUp',
        src: 'signUp',
        onDone: {
          target: '#' + constants.STATE_CONFIRM_EMAIL,
          actions: ['setEmailConfirmationDestination', 'assignAuthData'],
          cond: 'isUserConfirmed',
        },
        onError: {
          target: 'idle',
          actions: 'setError',
        },
      },
    },
  },
};

const logInStates = {
  id: constants.STATE_SIGNED_IN,
  initial: 'do_login' as const,
  exit: ['resetDelay', 'clearError'],
  states: {
    do_login: {
      entry: 'loading',
      exit: 'done',
      invoke: {
        id: 'logIn',
        src: 'logIn',
        onDone: [
          {
            target: '#' + constants.STATE_LOGGED_IN,
            actions: 'assignUser',
          },
        ],
        onError: [
          {
            target: '#' + constants.STATE_COMPLETE_PROFILE,
            cond: 'isMissingData',
            actions: 'assignPartialProfile',
          },
          {
            target: 'throttle',
            cond: 'isNetworkError',
          },
          {
            target: 'error',
            actions: 'setError',
          },
        ],
      },
    },
    throttle: {
      exit: 'increaseDelay',
      after: {
        SIGNIN_THROTTLE_DELAY: 'do_login',
      },
    },
    error: {
      exit: ['clearError', 'loading'],
      on: {
        RETRY: {
          target: 'throttle',
        },
        SIGNOUT: {
          actions: ['signOut'],
          target: '#' + constants.STATE_SIGNIN,
        },
      },
    },
  },
};

const confirmEmailStates = {
  id: constants.STATE_CONFIRM_EMAIL,
  initial: 'idle' as const,
  exit: ['clearError'],
  states: {
    idle: {
      exit: 'clearError',
      on: {
        RESEND_CONFIRM_EMAIL: {
          target: 'resend',
        },
        CONFIRM_EMAIL: {
          target: 'do_confirm',
          actions: 'assignConfirmationCode',
        },
      },
    },
    do_confirm: {
      entry: 'loading',
      exit: 'done',
      invoke: {
        id: 'confirmEmail',
        src: 'confirmEmail',
        onDone: {
          target: '#signIn',
        },
        onError: {
          target: 'idle',
          actions: 'setError',
        },
      },
    },
    resend: {
      entry: 'loading',
      exit: 'done',
      invoke: {
        id: 'resendConfirmation',
        src: 'resendConfirmation',
        onDone: {
          target: 'idle',
          actions: 'assignEmailConfirmationDestination',
        },
        onError: {
          target: 'idle',
          actions: 'setError',
        },
      },
    },
  },
};

const loggedInStates = {
  id: constants.STATE_LOGGED_IN,
  exit: ['signOut', 'clearContext', 'clearError'],
  initial: 'redirect' as const,
  states: {
    redirect: {
      entry: ['loading'],
      invoke: { id: 'noop', src: 'noop', onDone: 'idle' },
      exit: ['done'],
    },
    idle: {
      on: {
        SIGNOUT: '#' + constants.STATE_SIGNIN,
        REFRESH_USER: {
          actions: 'assignUser',
        },
        CLEAR_DESTINATION: {
          actions: ['clearDestination', 'forgetDestination'],
        },
      },
    },
  },
};

// The hierarchical (recursive) schema for the states
export interface AuthStateSchema {
  states: {
    check_token: {};
    [constants.STATE_COMPLETE_PROFILE]: {
      states: {
        idle: {};
        do_submit: {};
      };
    };
    [constants.STATE_SIGNIN]: {
      states: {
        idle: {};
        do_signin: {};
        maybe_email_not_confirmed: {};
      };
    };
    [constants.STATE_FORGOT_PASSWORD]: {
      states: {
        idle: {};
        send_code: {};
        resend_confirmation: {};
        choose_password: {};
        reset_password: {};
      };
    };
    [constants.STATE_SIGNUP]: {
      states: {
        idle: {};
        cognitoSignup: {};
      };
    };
    [constants.STATE_SIGNED_IN]: {
      states: {
        do_login: {};
        error: {};
      };
    };
    [constants.STATE_CONFIRM_EMAIL]: {
      states: {
        idle: {};
        do_confirm: {};
        resend: {};
      };
    };
    [constants.STATE_LOGGED_IN]: {
      states: {
        redirect: {};
        idle: {};
      };
    };
  };
}

type Role = {
  id: string;
  role: string;
  company: {
    id: string;
    name: string;
    slug: string;
  };
  project: {
    id: string;
    name: string;
    slug: string;
  };
};

type User = {
  user: {
    id: string;
    first_name: string;
    last_name: string;
    dni: string;
    email: string;
    phone: string;
    address: string;
  };
  roles: Array<Role>;
  invitations: Array<Invitation>;
};

// The events that the machine handles
export type AuthEvent =
  | { type: 'REFRESH_USER'; data: User }
  | { type: 'SIGNIN'; data: { username: string; password: string } }
  | {
      type: 'SIGNUP';
      data: {
        username: string;
        password: string;
        email: string;
        attributes: { first_name: string; last_name: string };
      };
    }
  | {
      type: 'CONFIRM_EMAIL';
      data: { code: string };
    }
  | {
      type: 'RESEND_CONFIRM_EMAIL';
    }
  | { type: 'SIGNOUT'; data?: { dontReload?: boolean } }
  | { type: 'RESET' }
  | { type: 'RETRY' }
  | { type: 'CLEAR_DESTINATION' }
  | { type: 'SUBMIT'; data: UpdateFederatedIdentityInformationInput }
  | {
      type: 'FORGOT_PASSWORD';
      data: { username: string; code?: string; new_password?: string };
    };

// The context (extended state) of the machine
export interface AuthContext extends defaultAuthContext {
  Auth: typeof Auth;
  Navigator: any;
  GraphQLClient: ApolloClient<NormalizedCacheObject>;
}

interface defaultAuthContext {
  username?: string;
  password?: string;
  email?: string;
  attributes?: {
    first_name: string;
    last_name: string;
    dni: string;
  };
  user?: MyUserDataFragment;
  authData?: CognitoUser;
  // authData: any;
  error?: Error;
  destination?: string;
  loading: boolean;
  emailConfirmationDestination?: string;
  emailConfirmationCode?: string;
  signinThrottleDelay: number;
  signinThrottleInitialDelay: number;
  signinThrottleFactor: number;
  forgotPasswordUsername?: string;
  forgotPasswordCode?: string;
  forgotPasswordNewPassword?: string;
  forgotPasswordDelivery?: any;
  partialProfile?: PartialProfileData;
}

export type MachineFactoryInputs = {
  Auth: typeof Auth;
  Navigator: any;
  GraphQLClient: ApolloClient<NormalizedCacheObject>;
};

export const defaultContext: defaultAuthContext = {
  loading: false,
  username: undefined,
  password: undefined,
  email: undefined,
  attributes: undefined,
  user: undefined,
  authData: undefined,
  error: undefined,
  destination: undefined,
  emailConfirmationDestination: undefined,
  emailConfirmationCode: undefined,
  signinThrottleDelay: 1000,
  signinThrottleInitialDelay: 1000,
  signinThrottleFactor: 1.5,
  forgotPasswordUsername: undefined,
  forgotPasswordCode: undefined,
  forgotPasswordNewPassword: undefined,
  forgotPasswordDelivery: {},
  partialProfile: undefined,
};

export const authMachine = createMachine({
  predictableActionArguments: true,
  id: 'auth',
  initial: 'check_token' as const,
  context: defaultContext as AuthContext,
  schema: { context: {} as AuthContext, events: {} as AuthEvent },
  states: {
    check_token: {
      id: 'check_token',
      invoke: {
        id: 'checkAuthenticatedUser',
        src: 'checkAuthenticatedUser',
        onDone: [
          {
            target: constants.STATE_SIGNED_IN,
            actions: 'assignAuthData',
          },
        ],
        onError: [
          {
            target: constants.STATE_SIGNUP,
            cond: 'isSignup',
          },
          {
            target: constants.STATE_SIGNIN,
          },
        ],
      },
    },
    [constants.STATE_COMPLETE_PROFILE]: {
      ...completeProfileStates,
    },
    [constants.STATE_SIGNIN]: {
      ...signInStates,
    },
    [constants.STATE_FORGOT_PASSWORD]: {
      ...forgotPasswordStates,
    },
    [constants.STATE_SIGNUP]: {
      ...signUpStates,
    },
    [constants.STATE_SIGNED_IN]: {
      ...logInStates,
    },
    [constants.STATE_CONFIRM_EMAIL]: {
      ...confirmEmailStates,
    },
    [constants.STATE_LOGGED_IN]: {
      ...loggedInStates,
    },
  },
});
